import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardList, ChevronRight, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { formatGs, ORDER_STATUS } from '@/utils/helpers';

export default function MyOrdersPage({ onAuthRequired }) {
  const { isAuthenticated } = useAuth();
  const { addItem, branchId, zone } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [repeatingId, setRepeatingId] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) { setLoading(false); return; }
    api.get('/pedidos/mis-pedidos').then(r => setOrders(r.data.orders)).catch(() => {}).finally(() => setLoading(false));
  }, [isAuthenticated]);

  const handleRepeatOrder = async (e, orderId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!zone) {
      toast.error('Seleccioná tu ubicación primero para repetir el pedido.');
      return;
    }

    setRepeatingId(orderId);
    try {
      const { data } = await api.get(`/pedidos/${orderId}/repetir?branch_id=${branchId}`);
      let added = 0;

      for (const item of data.disponibles) {
        addItem(
          { id: item.product_id, name: item.name, image_url: item.image_url },
          item.quantity,
          item.addons,
          item.unit_price
        );
        added++;
      }

      if (data.no_disponibles.length > 0) {
        const names = data.no_disponibles.map(i => i.product_name).join(', ');
        toast.warning(`No disponible: ${names}`, { duration: 4000 });
      }

      if (added > 0) {
        toast.success(`${added} producto${added > 1 ? 's' : ''} agregado${added > 1 ? 's' : ''} al carrito`);
        navigate('/carrito');
      } else {
        toast.error('Ningún producto del pedido está disponible actualmente.');
      }
    } catch {
      toast.error('Error al repetir el pedido.');
    } finally {
      setRepeatingId(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <div data-testid="my-orders-page" className="pb-nav flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <div className="w-20 h-20 rounded-full bg-zinc-900 flex items-center justify-center mb-4">
          <ClipboardList size={32} className="text-zinc-600" />
        </div>
        <h2 className="text-xl font-semibold">Mis pedidos</h2>
        <p className="text-zinc-500 text-sm mt-2">Ingresá para ver tu historial de pedidos</p>
        <button onClick={onAuthRequired} className="mt-6 bg-[#E84C1C] text-white font-medium px-8 py-3 rounded-full hover:bg-[#d43f12] transition-all">
          Iniciar sesión
        </button>
      </div>
    );
  }

  if (loading) return (
    <div className="pb-nav px-4 pt-4 space-y-3">
      {[1,2,3].map(i => <div key={i} className="h-24 bg-zinc-900 rounded-xl animate-pulse" />)}
    </div>
  );

  return (
    <div data-testid="my-orders-page" className="pb-nav">
      <div className="px-4 pt-4 pb-2">
        <h2 className="text-xl font-bold">Mis pedidos</h2>
        <p className="text-sm text-zinc-500">{orders.length} {orders.length === 1 ? 'pedido' : 'pedidos'}</p>
      </div>
      <div className="px-4 space-y-3 mt-2">
        {orders.map(o => {
          const st = ORDER_STATUS[o.status] || { label: o.status, color: 'bg-zinc-700 text-zinc-300' };
          const canRepeat = ['entregado', 'cancelado', 'rechazado'].includes(o.status);
          return (
            <div key={o.id} className="bg-zinc-900/50 rounded-xl border border-zinc-800/30 hover:border-zinc-700 transition-colors">
              <Link
                to={`/pedido/${o.id}`}
                data-testid={`order-${o.id}`}
                className="flex items-center gap-3 p-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold">Pedido #{o.id}</p>
                    <span className={`text-[10px] font-medium px-2.5 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">{new Date(o.created_at).toLocaleString('es-PY')}</p>
                  <p className="text-[#E84C1C] text-sm font-bold mt-1">{formatGs(o.total)}</p>
                </div>
                <ChevronRight size={18} className="text-zinc-600" />
              </Link>
              {canRepeat && (
                <div className="px-4 pb-3 -mt-1">
                  <button
                    data-testid={`repeat-order-${o.id}`}
                    onClick={(e) => handleRepeatOrder(e, o.id)}
                    disabled={repeatingId === o.id}
                    className="flex items-center gap-1.5 text-xs font-medium text-[#E84C1C] hover:text-[#ff6b3d] transition-colors disabled:opacity-50"
                  >
                    {repeatingId === o.id
                      ? <Loader2 size={13} className="animate-spin" />
                      : <RotateCcw size={13} />}
                    Repetir pedido
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {orders.length === 0 && (
          <div className="text-center py-12">
            <p className="text-zinc-500">Aún no tenés pedidos.</p>
            <Link to="/menu" className="text-[#E84C1C] text-sm mt-2 inline-block">Explorar menú</Link>
          </div>
        )}
      </div>
    </div>
  );
}
